import {getMonth, getYear, isSameMonth, isSameYear} from 'date-fns';
import {combine, createEvent, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {dateModel} from '@entities/date/model';
import {
    fetchNotificationStatuses,
    fetchViolationStatuses,
    fetchViolationTypes,
} from '@entities/violations/api';
import {DictionarySearchResponse, ViolationDictionaryItem} from '@entities/violations/types';

import * as api from './api';
import {AuditStatisticsEntity} from './entity';
import {AuditStatisticsDto} from './types';

const domain = rootDomain.createDomain('AuditStatistics');

export const AuditStatisticsGate = createGate('AuditStatistics');

const fetchStatisticsFx = domain.createEffect({handler: api.fetchStatistics});
const fetchViolationTypesFx = domain.createEffect({handler: fetchViolationTypes});
const fetchViolationStatusesFx = domain.createEffect({handler: fetchViolationStatuses});
const fetchNotificationStatusesFx = domain.createEffect({handler: fetchNotificationStatuses});

[
    fetchStatisticsFx,
    fetchViolationTypesFx,
    fetchViolationStatusesFx,
    fetchNotificationStatusesFx,
].forEach((fx) => fx.failData.watch(showRestError));

const selectedViolationTypeIds$ = domain.createStore<string[]>([]).reset(AuditStatisticsGate.close);
const statisticsDto$ = domain
    .createStore<AuditStatisticsDto | null>(null)
    .on(fetchStatisticsFx.doneData, (_, data) => data)
    .reset(AuditStatisticsGate.close);

const violationTypes$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(fetchViolationTypesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(AuditStatisticsGate.close);

const violationStatuses$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(fetchViolationStatusesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(AuditStatisticsGate.close);

const notificationStatuses$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(
        fetchNotificationStatusesFx.doneData,
        (_, data: DictionarySearchResponse) => data.data ?? [],
    )
    .reset(AuditStatisticsGate.close);

export const violationTypesChanged = createEvent<string[]>();
selectedViolationTypeIds$.on(violationTypesChanged, (_, ids) => ids);

sample({
    clock: AuditStatisticsGate.open,
    target: [fetchViolationTypesFx, fetchViolationStatusesFx, fetchNotificationStatusesFx],
});

sample({
    clock: merge([
        AuditStatisticsGate.open,
        dateModel.changed,
        violationTypesChanged,
        fetchViolationTypesFx.doneData,
    ]),
    source: combine({
        date: dateModel.state,
        violationTypes: violationTypes$,
        selectedViolationTypeIds: selectedViolationTypeIds$,
    }),
    filter: AuditStatisticsGate.status,
    fn: ({date, selectedViolationTypeIds}) => {
        const from = date.range.from ?? new Date();
        const to = date.range.to ?? from;
        const hasSingleMonth = isSameYear(from, to) && isSameMonth(from, to);

        return {
            year: getYear(from),
            ...(hasSingleMonth && {month: getMonth(from) + 1}),
            ...(selectedViolationTypeIds.length && {violationTypeIds: selectedViolationTypeIds}),
        };
    },
    target: fetchStatisticsFx,
});

const statistics$ = combine(statisticsDto$, violationTypes$, (statistics, violationTypes) => {
    const violationTypeCodes = violationTypes.map((item) => item.code);
    return new AuditStatisticsEntity(statistics, violationTypeCodes);
});

const pending$ = combine(
    [
        fetchStatisticsFx.pending,
        fetchViolationTypesFx.pending,
        fetchViolationStatusesFx.pending,
        fetchNotificationStatusesFx.pending,
    ],
    (list) => list.some(Boolean),
);

const $auditStatisticsState = combine({
    statistics: statistics$,
    violationTypes: violationTypes$,
    violationStatuses: violationStatuses$,
    notificationStatuses: notificationStatuses$,
    selectedViolationTypeIds: selectedViolationTypeIds$,
    pending: pending$,
});

export const auditStatisticsModel = {
    state: $auditStatisticsState,
    violationTypesChanged,
};
